import {
  Typography,
  Box,
  Slider,
  Button,
  Stack,
} from '@mui/material';
import { type HashatarConfig } from '@hashatar/core';

interface ConfigPanelProps {
  config: HashatarConfig;
  onConfigChange: (config: HashatarConfig) => void;
}

export default function ConfigPanel({
  config,
  onConfigChange,
}: ConfigPanelProps) {
  const handleSizeChange = (_: Event, value: number | number[]) => {
    onConfigChange({
      ...config,
      size: Array.isArray(value) ? value[0] : value,
    });
  };

  const PRESET_SIZES = [4, 8, 16, 32, 64];

  const handlePresetSize = (size: number) => {
    onConfigChange({
      ...config,
      size,
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Size Controls
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography gutterBottom>Avatar Size: {config.size}x{config.size}</Typography>
        <Slider
          value={config.size}
          onChange={handleSizeChange}
          min={4}
          max={128}
          step={1}
          marks={[
            { value: 4, label: '4' },
            { value: 16, label: '16' },
            { value: 32, label: '32' },
            { value: 64, label: '64' },
            { value: 128, label: '128' },
          ]}
          valueLabelDisplay="auto"
          sx={{
            height: 12,
            '& .MuiSlider-track': {
              height: 12,
              borderRadius: 6,
              backgroundColor: '#4caf50',
            },
            '& .MuiSlider-rail': {
              height: 12,
              borderRadius: 6,
              backgroundColor: '#e8f5e8',
            },
            '& .MuiSlider-thumb': {
              width: 24,
              height: 24,
              borderRadius: 6,
              backgroundColor: '#fff',
              border: '2px solid #4caf50',
              boxShadow: '0 0 8px rgba(76, 175, 80, 0.3)',
              '&:hover': {
                boxShadow: '0 0 12px rgba(76, 175, 80, 0.4)',
              },
              '&.Mui-focusVisible': {
                boxShadow: '0 0 12px rgba(76, 175, 80, 0.4)',
              },
            },
          }}
        />
      </Box>

      <Typography variant="subtitle2" gutterBottom>
        Quick Sizes:
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {PRESET_SIZES.map(size => (
          <Button
            key={size}
            variant={config.size === size ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handlePresetSize(size)}
            sx={{ 
              mb: 1,
              borderRadius: '4px',
              minWidth: '60px',
              backgroundColor: 'transparent',
              color: config.size === size ? '#2e7d32' : '#333',
              border: '1px solid #4caf50',
              borderColor: config.size === size ? '#2e7d32' : '#ddd',
              boxSizing: 'border-box',
              boxShadow: config.size === size ? '0 0 8px rgba(76, 175, 80, 0.3)' : 'none',
              fontWeight: 'normal',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#4caf50',
                boxShadow: '0 0 6px rgba(76, 175, 80, 0.2)',
              }
            }}
          >
            {size}x{size}
          </Button>
        ))}
      </Stack>
    </Box>
  );
}
